import { ReactNode, useState } from 'react'
import { Button } from '@/ui/components'
import { Container } from './Container'
import { cn } from '@/shared/utils'
import { Menu, X, MoveRight } from '@/ui/icons'
import { Link } from 'react-router-dom'

interface NavigationItem {
  title: string
  href?: string
  description?: string
  items?: NavigationItem[]
  icon?: ReactNode
}

interface NavigationProps {
  logo: ReactNode
  items: NavigationItem[]
  actions: {
    primary: {
      label: string
      href: string
    }
    secondary: {
      label: string
      href: string
    } | null
  }
  user?: {
    name: string
    email: string
    avatar?: string
  }
  onLogout?: () => void
  className?: string
  /** 'dark-glass' renders a translucent blurred bar for the dark landing page */
  appearance?: 'default' | 'dark-glass'
}

export function Navigation({ 
  logo, 
  items, 
  actions, 
  user,
  onLogout,
  className,
  appearance = 'default'
}: NavigationProps) {
  const [isOpen, setOpen] = useState(false)
  const isDarkGlass = appearance === 'dark-glass'

  return (
    <header
      className={cn(
        'w-full z-40 fixed top-0 left-0',
        isDarkGlass
          ? 'border-b border-white/10 bg-landing-base/70 backdrop-blur-md'
          : 'bg-gray-50 border-b',
        className
      )}
    >
      <Container size="app" className="relative min-h-20 flex gap-4 flex-row lg:grid lg:grid-cols-3 items-center">
        {/* Desktop Navigation */}
        <div className="justify-start items-center gap-4 lg:flex hidden flex-row">
          <nav className="flex justify-start items-start">
            <ul className="flex justify-start gap-4 flex-row">
              {items.map((item) => (
                <li key={item.title}>
                  {item.href ? (
                    <Button variant="ghost" asChild>
                      <Link to={item.href} className="flex items-center space-x-2">
                        {item.icon}
                        <span>{item.title}</span>
                      </Link>
                    </Button>
                  ) : (
                    <div className="relative group">
                      <Button variant="ghost" className="font-medium text-sm">
                        {item.title}
                      </Button>
                      {item.items && (
                        <div className="absolute top-full left-0 w-96 p-4 bg-background border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                          <div className="flex flex-col lg:grid grid-cols-2 gap-4">
                            <div className="flex flex-col h-full justify-between">
                              <div className="flex flex-col">
                                <p className="text-base font-medium">{item.title}</p>
                                <p className="text-muted-foreground text-sm">
                                  {item.description}
                                </p>
                              </div>
                              <Button size="sm" className="mt-4 w-fit">
                                Learn More
                              </Button>
                            </div>
                            <div className="flex flex-col text-sm h-full justify-end">
                              {item.items.map((subItem) => (
                                <Link
                                  key={subItem.title}
                                  to={subItem.href || '#'}
                                  className="flex flex-row justify-between items-center hover:bg-muted py-2 px-4 rounded"
                                >
                                  <span>{subItem.title}</span>
                                  <MoveRight className="w-4 h-4 text-muted-foreground" />
                                </Link>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* Logo */}
        <div className="flex lg:justify-center">
          {logo}
        </div>

        {/* Actions */}
        <div className="flex justify-end w-full gap-4">
          {user ? (
            // Authenticated user actions
            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-muted-foreground">
                    {user.name}
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={onLogout}>
                  Sign Out
                </Button>
              </div>
            </div>
          ) : (
            // Guest user actions - hide on mobile, show in menu
            <>
              {actions.secondary && (
                <Button variant="outline" asChild className="hidden lg:flex">
                  <Link to={actions.secondary.href}>{actions.secondary.label}</Link>
                </Button>
              )}
              <Button asChild className="hidden lg:flex">
                <Link to={actions.primary.href}>{actions.primary.label}</Link>
              </Button>
            </>
          )}

          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen(!isOpen)}
            className="lg:hidden"
          >
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <div
            className={cn(
              'absolute top-20 left-0 right-0 shadow-lg py-4 container',
              isDarkGlass
                ? 'border-t border-white/10 bg-landing-base/95 backdrop-blur-md'
                : 'bg-gray-50 border-t'
            )}
          >
            <div className="space-y-2">
              {items.map((item) => (
                <div key={item.title}>
                  <div className="flex flex-col gap-2">
                    {item.href ? (
                      <Link
                        to={item.href}
                        className="flex justify-between items-center"
                        onClick={() => setOpen(false)}
                      >
                        <span className="text-lg">{item.title}</span>
                        <MoveRight className="w-4 h-4 stroke-1 text-muted-foreground" />
                      </Link>
                    ) : (
                      <p className="text-lg">{item.title}</p>
                    )}
                    {item.items &&
                      item.items.map((subItem) => (
                        <Link
                          key={subItem.title}
                          to={subItem.href || '#'}
                          className="flex justify-between items-center"
                          onClick={() => setOpen(false)}
                        >
                          <span className="text-muted-foreground">
                            {subItem.title}
                          </span>
                          <MoveRight className="w-4 h-4 stroke-1" />
                        </Link>
                      ))}
                  </div>
                </div>
              ))}
              
              {/* Action buttons in mobile menu */}
              {!user && (
                <>
                  <div className="border-t pt-4 mt-4">
                    {actions.secondary && (
                      <Button variant="outline" asChild className="w-full justify-start mb-2">
                        <Link to={actions.secondary.href} onClick={() => setOpen(false)}>
                          {actions.secondary.label}
                        </Link>
                      </Button>
                    )}
                    <Button asChild className="w-full justify-start">
                      <Link to={actions.primary.href} onClick={() => setOpen(false)}>
                        {actions.primary.label}
                      </Link>
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </Container>
    </header>
  )
}
